import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { AbilityService } from '../services/ability/ability.service';
import { NotificationService } from '../services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {
  constructor(
    private ability: AbilityService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    // CST-style scoped-kiosk sessions (isSSO AND mainLabUrl) are gated by
    // SsoScopeGuard, not by role-based PermissionGuard. Bypass here so we
    // don't race the /me user-role population on initial navigation.
    //
    // Full SSO logins (mainLabUrl null) are regular users authenticated via
    // SSO. They need role-based checks like any local user; without them a
    // Viewer could reach admin pages.
    if (this.ability.isSSO && this.ability.mainLabUrl) {
      return true;
    }

    const subjectName = route.data['subject'];
    const action = route.data['action'] || 'view';

    if (!subjectName) {
      return true;
    }

    const subject = this.ability.subjects[subjectName];

    if (!subject) {
      console.error(`PermissionGuard: Subject '${subjectName}' not found.`);
      return false;
    }

    let hasPermission = false;

    if (action === 'view') {
      hasPermission = this.ability.canView(subject);
    } else if (action === 'edit') {
      hasPermission = this.ability.canEdit(subject);
    } else {
      hasPermission = this.ability.can(action, subject);
    }

    if (!hasPermission) {
      this.notificationService.error('You do not have permission to access this page.');
      this.router.navigate([this.ability.defaultPage()]);
      return false;
    }

    return true;
  }
}